import cx from 'classnames'
import { useContext, useMemo } from 'react'

import { SanityImageFragment } from '@data/sanity/queries/types/image'
import {
  SanityPrintAreaType,
  SanityPrintArea,
  SanityPrintSlotType,
} from '@data/sanity/queries/types/print'
import { SanityPrintColor } from '@data/sanity/queries/types/shop'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { getPrintPreviewText } from '@lib/print/text'
import Photo from '@components/photo'

interface UploadStepPrintPreviewAreaProps {
  area: SanityPrintArea
  printSlotType: SanityPrintSlotType
  printColor?: SanityPrintColor
  previewImage?: SanityImageFragment
}

const UploadStepPrintPreviewArea = ({
  area,
  printSlotType,
  printColor,
  previewImage,
}: UploadStepPrintPreviewAreaProps) => {
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const previewString = useMemo(() => {
    return getPrintPreviewText(shopFormStrings, printSlotType, area.type)
  }, [area, printSlotType, shopFormStrings])

  const isTypeImage =
    area.type === SanityPrintAreaType.LOGO ||
    area.type === SanityPrintAreaType.IMAGE
  const hasPreviewImage = isTypeImage && !!previewImage
  const hasBorder = isTypeImage && !previewImage

  return (
    <div
      className={cx(
        'absolute flex justify-center items-center pointer-events-none z-10',
        {
          'text-white': printColor === 'light',
          'text-black': printColor === 'dark',
          'border-white/50': hasBorder && printColor === 'light',
          'border-black/50': hasBorder && printColor === 'dark',
          'border border-dashed': hasBorder,
          'rounded-full': hasBorder && area.type === SanityPrintAreaType.LOGO,
          'rounded-md': hasBorder && area.type === SanityPrintAreaType.IMAGE,
        },
      )}
      style={{
        width: `${area.width}%`,
        height: `${area.height}%`,
        left: `${area.x - area.width / 2}%`,
        top: `${area.y - area.height / 2}%`,
      }}
    >
      {hasPreviewImage && <Photo image={previewImage} />}
      {!hasPreviewImage && (
        <span className="text-center font-bold text-xs">{previewString}</span>
      )}
    </div>
  )
}

export default UploadStepPrintPreviewArea
